<template>
  <div class="action-inspector">
    <el-divider class="action-inspector__divider" />
    <el-form
      ref="ruleFormRef"
      :model="ruleForm"
      :rules="rules"
      label-position="top"
      class="action-inspector__form"
      size="small"
    >
      <el-form-item label="Test Name (ID)" prop="title">
        <el-input v-model="ruleForm.title" />
      </el-form-item>
      <el-form-item label="Application" prop="application_id">
        <el-select v-model="ruleForm.application_id" placeholder="" filterable>
          <el-option
            v-for="application in applications"
            :key="application.id"
            :label="application.title"
            :value="application.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Unique Test Key" prop="key">
        <el-input v-model="ruleForm.key" />
      </el-form-item>
      <el-form-item label="Target (ID)" prop="target">
        <el-input v-model="ruleForm.target" />
      </el-form-item>
      <el-form-item label="Description">
        <div
          id="editorjs"
          ref="cdx"
          class="cdx-input action-inspector__cdx scroll"
          :class="{ 'action-inspector__cdx_error': isErrorCdx }"
        ></div>
      </el-form-item>
      <el-form-item label="">
        <el-checkbox-group v-model="ruleForm.platforms_ids">
          <el-checkbox
            v-for="platform in platforms"
            :key="platform.id"
            :label="platform.title"
            :value="platform.id"
          />
        </el-checkbox-group>
      </el-form-item>
      <el-button
        :icon="Plus"
        size="small"
        :disabled="ruleForm.variants.length >= 5"
        @click="addVariant"
        >Add variant</el-button
      >
      <CardVariant
        v-for="(variant, index) in ruleForm.variants"
        :key="variant.id"
        :index="index"
        :unique="variant.key"
        :title="variant.title"
        :percentage="variant.percentage"
        :error="variant.error"
        :isRemovable="isRemovableVariants"
        @update="onUpdateVariant(...$event, variant.id)"
        @remove="removeVariant(variant.id, variant?.status)"
      />
    </el-form>
    <div class="action-inspector__bottom">
      <div class="action-inspector__percentage-wrapper">
        <div class="action-inspector__percentage-title">
          Traffic Percentage for Variants (Balance)
        </div>
        <RangeVariant
          @change-variants="changePercentage"
          :variants="ruleForm.variants"
        />
      </div>
      <div class="action-inspector__buttons">
        <div class="action-inspector__button-left">
          <el-button @click="onSubmit(ruleFormRef)">Save</el-button>
          <el-button link @click="onCancel">Cancel</el-button>
        </div>
        <el-button
          v-if="status === 'edit'"
          class="action-inspector__button-delete"
          :icon="Delete"
          @click="onDelete"
        ></el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  defineProps,
  defineEmits,
  watch,
  onMounted,
} from "vue";
import { useStore } from "vuex";
import { Delete, Plus } from "@element-plus/icons-vue";
import { CardVariant } from "@/shared/ui/components/CardVariant";
import { RangeVariant } from "@/shared/ui/components/RangeVariant";
import EditorJS from "@editorjs/editorjs";

const store = useStore();

const props = defineProps({
  field: {
    type: Object,
    default: () => ({}),
  },
  status: {
    type: String,
    required: true,
    validator(value) {
      return ["edit", "create"].includes(value);
    },
  },
});

const emit = defineEmits(["submit", "cancel", "delete"]);

const ruleFormRef = ref();
const editor = ref();
const cdx = ref();
const isErrorCdx = ref(false);

const ruleForm = reactive({
  title: "",
  application_id: "",
  key: "",
  target: "",
  description: "",
  platforms_ids: [],
  variants: [],
  status: "draft",
});

const rules = reactive({
  title: [{ required: true, trigger: "blur", message: "" }],
  application_id: [{ required: true, trigger: "blur", message: "" }],
  key: [{ required: true, trigger: "blur", message: "" }],
  target: [{ required: true, trigger: "blur", message: "" }],
  // platforms_ids: {
  //   type: "array",
  //   required: true,
  //   message: "",
  // },
});

watch(
  () => props.field,
  (field) => {
    initForm(field);
  },
  { deep: true }
);

const platforms = computed(() => {
  return store.state.platforms.list;
});

const applications = computed(() => {
  return store.state.applications.list;
});

const isRemovableVariants = computed(() => {
  return ruleForm.variants.length > 2;
});

onMounted(async () => {
  initForm();

  if (cdx.value) {
    editor.value = new EditorJS({
      holder: cdx.value,
      data: {
        blocks: [
          {
            type: "paragraph",
            data: {
              text: ruleForm.description,
            },
          },
        ],
      },
    });
  }
});

async function onSubmit(formEl) {
  if (!formEl) return;
  await editor.value.save().then((savedData) => {
    const convertedHtml = savedData.blocks.reduce((acc, block) => {
      switch (block.type) {
        case "paragraph":
          acc += `<p>${block.data.text}</p>`;
          break;
        default:
          break;
      }
      return acc;
    }, "");

    ruleForm.description = convertedHtml;
  });
  isErrorCdx.value = ruleForm.description.length ? false : true;

  ruleForm.variants.forEach((item) => {
    const isValid = isValidItem(item);
    if (!isValid) {
      item.error = true;
    } else {
      item.error = false;
    }
  });
  const isErrorVariants = ruleForm.variants.some((variant) => variant.error);
  formEl.validate((valid) => {
    if (valid && !isErrorCdx.value && !isErrorVariants) {
      emit("submit", ruleForm);
    } else {
      return false;
    }
  });
}

function onDelete() {
  emit("delete", props.field.id);
}

function onCancel() {
  initForm();
  emit("cancel");
}

const addVariant = () => {
  ruleForm.variants.push({
    id: Date.now(),
    title: "",
    key: "",
    percentage: 0,
    status: "new",
  });

  recalcEntireVariant();
};

const changePercentage = (arr = []) => {
  arr.forEach((item, index) => {
    ruleForm.variants[index].percentage = item.percentage;
    if (ruleForm.variants[index].status !== "new") {
      ruleForm.variants[index].status = "edit";
    }
  });
};

const recalcVariant = () => {
  ruleForm.variants = ruleForm.variants.reduce((acc, variant, index, array) => {
    if (index && index === array.length - 1) {
      acc.push({
        ...variant,
        count: 100,
      });
    } else if (acc.length) {
      acc.push({
        ...variant,
        count: +acc[acc.length - 1].count + variant.percentage,
      });
    } else {
      acc.push({
        ...variant,
        count: variant.percentage,
      });
    }
    return acc;
  }, []);
};
const recalcEntireVariant = () => {
  ruleForm.variants = ruleForm.variants.reduce((acc, variant, index, array) => {
    if (index && index === array.length - 1) {
      acc.push({
        ...variant,
        count: 100,
        percentage: +(100 / array.length).toFixed(0),
      });
    } else if (acc.length) {
      acc.push({
        ...variant,
        count: +acc[acc.length - 1].count + +(100 / array.length).toFixed(0),
        percentage: +(100 / array.length).toFixed(0),
      });
    } else {
      acc.push({
        ...variant,
        count: array.length ? Math.ceil(+(100 / array.length)) : 100,
        percentage: array.length ? Math.ceil(+(100 / array.length)) : 100,
      });
    }
    return acc;
  }, []);
};
const removeVariant = (id) => {
  ruleForm.variants = ruleForm.variants.filter((variant) => variant.id !== id);
  recalcVariant();
};

const onUpdateVariant = (val, key, id) => {
  const variant = ruleForm.variants.find((item) => item.id === id);
  variant[key] = val;
  if (variant.status !== "new") {
    variant.status = "edit";
  }
};

const isValidItem = (item) => {
  return item.title && item.key;
};

const initForm = () => {
  ruleForm.title = props.field.title;
  ruleForm.application_id = props.field.application_id;
  ruleForm.status = props.field.status;
  ruleForm.description = props.field.description;
  ruleForm.target = props.field.target;
  ruleForm.key = props.field.key;
  ruleForm.variants = props.field.variants || [];
  ruleForm.platforms_ids = props.field.platforms_ids;

  if (cdx.value && editor.value) {
    editor.value.render({
      blocks: [
        {
          type: "paragraph",
          data: {
            text: ruleForm.description,
          },
        },
      ],
    });
  }

  if (props.status === "edit") {
    recalcVariant();
  } else if (props.status === "create") {
    recalcEntireVariant();
  }
};
</script>

<style lang="scss">
.action-inspector {
  overflow: auto;
  height: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-bottom: 186px;
  &::-webkit-scrollbar {
    display: none;
  }
  .el-form-item {
    margin-bottom: 8px !important;
  }
  .el-form-item__label {
    margin-bottom: 0 !important;
  }

  &__divider {
    margin: 16px 0 !important;
  }

  &__cdx {
    height: 84px;
    overflow: hidden;
    overflow-y: auto;
    padding: 2px 8px;
    border-radius: var(--Radius-border-radius-base, 4px);
    box-shadow: 0 0 0 1px var(#dcdfe6, #dcdfe6) inset;
    // border: 1px solid var(--Color-Border-border-color, #dcdfe6);
    background: var(--Color-Fill-fill-color-blank, #fff);
    .codex-editor__redactor {
      margin: 0;
      padding-bottom: 0 !important;
    }
    .ce-toolbar {
      display: none;
    }
    &_error {
      border-color: #f76141;
    }
  }
  &__bottom {
    border-top: 1px solid #e4e7ed;
    background-color: rgb(242, 243, 245);
    padding: 24px;
    width: 100%;
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: 1;
  }
  &__buttons {
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__button-delete .el-icon {
    color: rgba(245, 108, 108, 1);
  }
  &__percentage-title {
    margin-bottom: 2px;
    color: var(--Color-Text-text-color-regular, #606266);
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
}
</style>
